'use client'

import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useMemo, useState } from 'react'

import { pivotScenario, type ScenarioGrid } from '@/features/home/lib/home-scenario'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { positionToRiskSimulationPosition } from '@/features/risk-slides/lib/risk-slides-util'
import { useCapabilities } from '@/hooks/use-capabilities'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { BillingFeature } from '@/lib/billing/capabilities'
import { getInstrumentMapForExchange } from '@/lib/utils/exchange-instrument-map'
import { runRiskSimulation } from '@/service/risk.api'
import { positionsRawAtom } from '@/state/account'
import {
  OptionPriceSource,
  type RiskSimulationRequest,
  SimulationPriceRangeKind,
} from '@/types/generated/public_api'

const DAY_MS = 86_400_000
/** The price range simulated, in percent either side of today's price. */
export const SCENARIO_RANGE = 20
/** 2.5% columns: enough points for a smooth curve, every other one a 5% step. */
const PRICE_STEPS = 16
/** Today, then weekly for four weeks (options decay between them). */
const WEEKS = 4

function at8Utc(ms: number): string {
  const date = new Date(ms)
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 8)
  ).toISOString()
}

/**
 * Risk Slides' simulation of the user's open positions in `asset` over ±20% price moves,
 * today and weekly for four weeks, priced by the server at marks (same request the Risk Slides
 * page builds for a coin with no manual legs). Behind the Risk Simulation plan feature.
 */
export function useHomeScenario(asset: string | null): {
  grid: ScenarioGrid | null
  allowed: boolean
  hasPositions: boolean
  isPending: boolean
  isError: boolean
} {
  const positions = useAtomValue(positionsRawAtom)
  const instrumentMap = useMultiExchangeInstrumentMap()
  const { hasFeature } = useCapabilities()
  const allowed = hasFeature(BillingFeature.RiskSimulation)
  const [now] = useState(() => Date.now())

  const portfolio = useMemo(() => {
    if (!asset) return []
    return positions
      .filter((position) => {
        const map = getInstrumentMapForExchange(instrumentMap, position.exchange)
        return resolveBaseCurrency(map, position.symbol) === asset
      })
      .map((position) =>
        positionToRiskSimulationPosition(
          position,
          getInstrumentMapForExchange(instrumentMap, position.exchange)
        )
      )
      .filter((leg) => leg.size !== 0 && leg.entryPrice > 0)
  }, [asset, positions, instrumentMap])

  // Re-simulate when the legs change, not on every mark tick.
  const signature = portfolio
    .map((leg) => `${leg.size}@${leg.entryPrice}:${JSON.stringify(leg.instrumentType)}`)
    .sort()
    .join('|')

  const query = useQuery({
    queryKey: ['home-scenario', asset, signature],
    queryFn: () => {
      const request: RiskSimulationRequest = {
        asset: asset ?? '',
        portfolio,
        optionPriceSource: OptionPriceSource.OPTION_PRICE_SOURCE_MARK,
        simulationGrid: {
          valueLower: -SCENARIO_RANGE / 100,
          valueUpper: SCENARIO_RANGE / 100,
          priceSteps: PRICE_STEPS,
          volBumpPct: 0,
          volBumpsByExpiry: {},
          startDate: at8Utc(now),
          endDate: at8Utc(now + WEEKS * 7 * DAY_MS),
          timeStepSeconds: String((7 * DAY_MS) / 1000),
          priceRangeKind: SimulationPriceRangeKind.SIMULATION_PRICE_RANGE_KIND_PERCENT,
        },
        accountMargins: [], // server-populated, as in risk-slides-table.tsx
      }
      return runRiskSimulation(request)
    },
    enabled: allowed && Boolean(asset) && portfolio.length > 0,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const grid = useMemo(
    () =>
      query.data ? pivotScenario(query.data.resultsUsd, -SCENARIO_RANGE, SCENARIO_RANGE) : null,
    [query.data]
  )

  return {
    grid,
    allowed,
    hasPositions: portfolio.length > 0,
    isPending: allowed && portfolio.length > 0 && query.isPending,
    isError: query.isError,
  }
}
